import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Location } from '@vendasta/listing-products';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  KeywordStore,
  MaxRank,
  SEARCH_RADIUS_LOCAL,
  SEODataSearchRadiusZoomValue,
  SeoDataSettings,
  SEOTableData,
} from '../local-seo';

interface ColumnConfigValue {
  name: string;
  class: string;
  iconURL?: string;
  showDelta?: boolean;
  deltaColumn?: string;
}

export interface ColumnConfig {
  [key: string]: ColumnConfigValue;
}

interface SortSettings {
  active: keyof SEOTableData;
  direction: 'asc' | 'desc';
}

export interface Stat {
  value: string;
  rawValue: number;
}

export const SEODATA_TAB_GRAPH = 'graph';
export const SEODATA_TAB_GRID = 'grid';

@Component({
  selector: 'local-seo-keyword-table',
  templateUrl: './local-seo-table.component.html',
  styleUrls: ['./local-seo-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*', minHeight: '60px' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('rotateIcon', [
      state('down', style({ transform: 'rotate(0)' })),
      state('up', style({ transform: 'rotate(-180deg)' })),
      transition('down <=> up', animate('225ms ease-out')),
    ]),
  ],
  standalone: false,
})
export class LocalSEOTableComponent implements OnInit, OnDestroy {
  sortSettings = <SortSettings>{ active: 'localRank', direction: 'asc' };
  dataSource: MatTableDataSource<SEOTableData> = new MatTableDataSource([]);

  @Input() set SEOData(data: SEOTableData[]) {
    if (!data?.length) {
      return;
    }
    this.dataSource.data = data;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortData = (data: SEOTableData[], sort: MatSort) => {
      const favorites = this.sortData(
        data.filter((item) => item.isFavorite),
        sort,
      );
      const nonFavorites = this.sortData(
        data.filter((item) => !item.isFavorite),
        sort,
      );
      return [...favorites, ...nonFavorites];
    };
    if (!this.expandedData) {
      const sortedData = this.dataSource.sortData(data, this.sort);
      this.expandedData = sortedData[0];
    }
  }

  @Input() loading$: Observable<boolean>;
  @Input() keywordStore: KeywordStore;
  @Input() emptyStateImageURL: string;
  @Input() hideDropdown: boolean;
  @Input() proActive: boolean;
  @Input() location: Location;
  @Input() agid: string;
  @Input() settings: SeoDataSettings;
  @Input() disableEditingKeywords: boolean;
  @Input() editionUpgradeHidden: boolean;
  @Output() upgradeButtonClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() deleteKeyword: EventEmitter<string> = new EventEmitter<string>();
  @Output() favoriteKeywordsChanged: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() syncingKeywordsChanged: EventEmitter<string[]> = new EventEmitter<string[]>();
  selectedTab$: Observable<string>;
  subscriptions: Subscription[] = [];
  private selectedTab$$: BehaviorSubject<string> = new BehaviorSubject<string>(SEODATA_TAB_GRID);
  private paginator: MatPaginator;
  private sort: MatSort;
  expandedData: SEOTableData | null;
  activeTab: string;

  maxRank = MaxRank;

  columnConfig: ColumnConfig = {
    keyword: {
      name: 'LOCAL_SEO.KEYWORD.TITLE',
      class: '',
    },
    local: {
      name: 'LOCAL_SEO.LOCAL_SEO.MAPS',
      class: 'divided',
      iconURL: 'https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/google/google-maps.svg',
    },
    organic: {
      name: 'LOCAL_SEO.LOCAL_SEO.ORGANIC',
      class: 'divided',
      iconURL: 'https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/google/google.png',
    },
    localRank: {
      name: 'LOCAL_SEO.LOCAL_SEO.RANK',
      class: 'divided',
      showDelta: true,
      deltaColumn: 'localStartedAt',
    },
    organicRank: {
      name: 'LOCAL_SEO.LOCAL_SEO.RANK',
      class: 'divided',
      showDelta: true,
      deltaColumn: 'organicStartedAt',
    },
    localStartedAt: {
      name: 'LOCAL_SEO.LOCAL_SEO.STARTED_AT',
      class: '',
    },
    organicStartedAt: {
      name: 'LOCAL_SEO.LOCAL_SEO.STARTED_AT',
      class: '',
    },
    difficulty: {
      name: 'LOCAL_SEO.LOCAL_SEO.RANK_DIFFICULTY',
      class: 'divided',
    },
    searchVolume: {
      name: 'LOCAL_SEO.LOCAL_SEO.SEARCH_VOLUME',
      class: 'mat-header-wrap-text',
    },
    empty: {
      name: '',
      class: '',
    },
    empty2: {
      name: '',
      class: '',
    },
  };
  statColumns: string[] = [
    'localRank',
    'localStartedAt',
    'organicRank',
    'organicStartedAt',
    'difficulty',
    'searchVolume',
  ];
  topColumns: string[] = ['empty', 'local', 'organic', 'empty2', 'expandIcon'];
  columns: string[] = [
    'keyword',
    'localRank',
    'localStartedAt',
    'organicRank',
    'organicStartedAt',
    'difficulty',
    'searchVolume',
    'expandIcon',
  ];

  constructor(
    public translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    this.selectedTab$ = this.selectedTab$$.asObservable();
  }

  @ViewChild(MatSort)
  set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }

  @ViewChild(MatPaginator)
  set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  setDataSourceAttributes(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onSelectActiveTab(tab: string): void {
    this.selectedTab$$.next(tab);
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.selectedTab$.subscribe((tab: string) => {
        this.activeTab = tab;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  setSortSettings($event: SortSettings): void {
    this.sortSettings = { ...$event };
  }

  getZoomLevel(radius: number): number {
    return SEODataSearchRadiusZoomValue[radius ?? SEARCH_RADIUS_LOCAL];
  }

  updateSyncingKeywords(keyword: string, checked: boolean): void {
    if (checked) {
      this.syncingKeywordsChanged.emit([...this.keywordStore.syncingKeywords, keyword]);
    } else {
      this.syncingKeywordsChanged.emit(
        this.keywordStore?.syncingKeywords?.filter((syncingKeywords) => syncingKeywords !== keyword),
      );
    }
  }

  updateFavoriteKeywords(keyword: string, checked: boolean): void {
    if (checked) {
      this.favoriteKeywordsChanged.emit([...this.keywordStore.favoriteKeywords, keyword]);
    } else {
      this.favoriteKeywordsChanged.emit(
        this.keywordStore?.favoriteKeywords?.filter((favKeyword) => favKeyword !== keyword),
      );
    }
  }

  sortData(data: SEOTableData[], sort: MatSort): SEOTableData[] {
    const active = sort?.active;
    const direction = sort?.direction ?? '';
    if (!active || direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const valueA = getValuesForSorting(a, active);
      const valueB = getValuesForSorting(b, active);
      const comparatorResult = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      return comparatorResult * (direction === 'asc' ? 1 : -1);
    });
  }
}

export function getValuesForSorting(data: SEOTableData, sortHeaderId: string): string | number {
  const value = data[sortHeaderId];
  switch (sortHeaderId) {
    case 'keyword':
      return (value as Stat).value.toLocaleLowerCase();
    default:
      return (value as Stat).rawValue ?? 999999; //set undefined as the highest possible value so it remains at the bottom
  }
}
